import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Addchart,
  CameraOutlined,
  CreateNewFolder,
  Description,
  Extension,
  ImageAspectRatio,
  InsertDriveFileOutlined,
  MonetizationOn,
  UploadFile,
} from "@mui/icons-material";
import { Backdrop, Button, CircularProgress, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { selectCurrentAccount, selectFolderById, selectFoldersByParentId, useCreateProjectMutation } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useCreatePermissionCheck } from "fond/hooks/useCreatePermissionCheck";
import { NewFolder } from "fond/projects";
import NewCityPlanner from "fond/projects/CityPlanner/NewCityPlanner";
import { ProjectSubtype, ProjectType } from "fond/types";
import { NewItemSource } from "fond/types/analytics";
import { useAppSelector } from "fond/utils/hooks";
import { Modal, SubscriptionErrorMessage } from "fond/widgets";

interface RouteParams {
  folderId: string;
}

type NewItemListProps = {
  /**
   * An item was clicked
   */
  onItemClick?: () => void;
  showDividers?: boolean;
  /**
   * A source for analytics event tracking
   */
  source: NewItemSource;
};

const NewItemList: React.FC<NewItemListProps> = ({ onItemClick, showDividers = false, source }: NewItemListProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [currentModal, setCurrentModal] = useState<null | "folder" | "cityPlanner" | "subscriptionError">(null);
  const { folderId } = useParams<keyof RouteParams>();
  const [createProject, { isLoading }] = useCreateProjectMutation();
  const {
    canAddFolder,
    canCreateProject,
    canImportReport,
    canWriteCityPlannerProject,
    canWritePlannerProjects,
    canWritePlannerExtendedProjects,
    hasNBNModule,
  } = useCreatePermissionCheck();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const { value: showImportReportOption } = useFeatureFlag("import_report");
  const { value: showCityPlannerOption } = useFeatureFlag("city_planner_foundations");
  const { value: showNBNAutoDesignOption } = useFeatureFlag("nbn_auto_design");

  const folder = useAppSelector((state) => (folderId ? selectFolderById(state, folderId) : undefined));
  const siblings = useAppSelector((state) => selectFoldersByParentId(state, folderId ?? null));

  /**
   * Request project creation and navigate to project page on success.
   */
  const createAndOpenProject = async (projectType: ProjectType, subType?: ProjectSubtype): Promise<void> => {
    if (!currentAccount) {
      enqueueSnackbar("There was an issue creating this project. Please try again.", { variant: "error" });
      return;
    }

    try {
      const project = await createProject({ folderId, accountId: currentAccount.ID, type: projectType, subType }).unwrap();
      analytics.track("New Item.Project.Created project", { projectId: project.ID, projectType, source });
      navigate(`/project/${project.ID}/`);
    } catch (e) {
      enqueueSnackbar("There was an issue creating this project. Please try again.", { variant: "error" });
      Sentry.captureException(e);
    }
  };

  const onCreateCityPlannerClick = async (): Promise<void> => {
    analytics.track("new-item.market-lens.clicked", { source });
    setCurrentModal("cityPlanner");
  };

  const onCreateFolderClick = () => {
    analytics.track("new-item.folder.clicked", { source });
    setCurrentModal("folder");
  };

  useEffect(() => {
    if (currentModal !== null && onItemClick) {
      onItemClick();
    }
  }, [currentModal, onItemClick]);

  const mainBlue = theme.palette.primary.main;

  const divider = useMemo(() => (showDividers ? <Divider /> : null), [showDividers]);

  if (!canAddFolder && !canCreateProject) {
    return null;
  }

  return (
    <>
      <Backdrop open={isLoading} sx={{ position: "absolute", zIndex: "drawer", backgroundColor: "rgba(255,255,255, 0.5)" }}>
        <CircularProgress />
      </Backdrop>
      <MenuList sx={{ color: mainBlue }} component="nav">
        <MenuItem
          className="create-folder"
          data-testid="create-folder-option"
          aria-disabled={!canAddFolder}
          disabled={!canAddFolder}
          onClick={onCreateFolderClick}
        >
          <ListItemIcon>
            <CreateNewFolder fontSize="small" sx={{ color: mainBlue }} />
          </ListItemIcon>
          <ListItemText>Folder</ListItemText>
        </MenuItem>

        {divider}

        <MenuItem
          className="create-planner"
          data-testid="create-planner-option"
          aria-disabled={!canCreateProject}
          disabled={!canCreateProject}
          onClick={() => {
            if (canWritePlannerProjects) {
              createAndOpenProject("planner");
            } else {
              setCurrentModal("subscriptionError");
            }
          }}
        >
          <ListItemIcon>
            <Description fontSize="small" sx={{ color: mainBlue }} />
          </ListItemIcon>
          <ListItemText>Planner Project</ListItemText>
        </MenuItem>
        {/*
          Currently disabled until further notice
          {showDesignProjectOption && (
            <MenuItem
              className="create-design"
              data-testid="create-small-design-option"
              aria-disabled={!canCreateProject}
              disabled={!canCreateProject}
              onClick={() => createAndOpenProject("design", "irby_small_batch")}
            >
              <ListItemIcon>
                <ImageAspectRatio fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Small Design</ListItemText>
            </MenuItem>
          )}
         */}
        {canWritePlannerExtendedProjects && (
          <MenuItem
            className="create-planner-extended"
            data-testid="create-planner-extended"
            aria-disabled={!canCreateProject}
            disabled={!canCreateProject}
            onClick={() => createAndOpenProject("planner", "planner_extended")}
          >
            <ListItemIcon>
              <ImageAspectRatio fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Fond Design</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          className="create-collaboration"
          data-testid="create-collaboration-option"
          aria-disabled={!canCreateProject}
          disabled={!canCreateProject}
          onClick={() => createAndOpenProject("collaboration")}
        >
          <ListItemIcon>
            <InsertDriveFileOutlined fontSize="small" sx={{ color: mainBlue }} />
          </ListItemIcon>
          <ListItemText>Collaboration Project</ListItemText>
        </MenuItem>

        {showCityPlannerOption && (
          <>
            {divider}
            <MenuItem
              className="create-city-planner"
              data-testid="create-city-planner-option"
              aria-disabled={!canWriteCityPlannerProject}
              disabled={!canWriteCityPlannerProject}
              onClick={onCreateCityPlannerClick}
            >
              <ListItemIcon>
                <CameraOutlined fontSize="small" sx={{ color: mainBlue }} />
              </ListItemIcon>
              <ListItemText>Market Lens Project</ListItemText>
            </MenuItem>
          </>
        )}

        {hasNBNModule && (
          <>
            <MenuItem
              className="create-nbn-cost-to-serve"
              data-testid="create-nbn-cost-to-serve-option"
              aria-disabled={!canCreateProject}
              disabled={!canCreateProject}
              onClick={() => {
                if (canWritePlannerProjects) {
                  createAndOpenProject("design", "nbn_cost_to_serve");
                } else {
                  setCurrentModal("subscriptionError");
                }
              }}
            >
              <ListItemIcon>
                <MonetizationOn fontSize="small" sx={{ color: mainBlue }} />
              </ListItemIcon>
              <ListItemText>Cost to Serve</ListItemText>
            </MenuItem>
            {showNBNAutoDesignOption && (
              <MenuItem
                className="create-nbn-idd-lite"
                data-testid="create-nbn-idd-lite-option"
                aria-disabled={!canCreateProject}
                disabled={!canCreateProject}
                onClick={() => {
                  if (canWritePlannerProjects) {
                    createAndOpenProject("design", "nbn_idd_lite");
                  } else {
                    setCurrentModal("subscriptionError");
                  }
                }}
              >
                <ListItemIcon>
                  <Extension fontSize="small" sx={{ color: mainBlue }} />
                </ListItemIcon>
                <ListItemText>NBN Auto Design</ListItemText>
              </MenuItem>
            )}
          </>
        )}

        {divider}

        <MenuItem
          className="create-report"
          data-testid="create-report-option"
          onClick={() => {
            analytics.track("New Item.Report.Create report menu item clicked", { source });
            navigate({ pathname: "/reports/create", search: folderId && new URLSearchParams({ folderId }).toString() });
          }}
        >
          <ListItemIcon>
            <Addchart fontSize="small" sx={{ color: mainBlue }} />
          </ListItemIcon>
          <ListItemText>Report</ListItemText>
        </MenuItem>

        {showImportReportOption && canImportReport && (
          <MenuItem
            className="import-report"
            data-testid="import-report-option"
            onClick={() => {
              analytics.track("New Item.Import Report.Import report menu item clicked", { source });
              navigate({ pathname: "/reports/import", search: folderId && new URLSearchParams({ folderId }).toString() });
            }}
          >
            <ListItemIcon>
              <UploadFile fontSize="small" sx={{ color: mainBlue }} />
            </ListItemIcon>
            <ListItemText>Import Report</ListItemText>
          </MenuItem>
        )}
      </MenuList>

      {currentModal === "folder" && <NewFolder onClose={() => setCurrentModal(null)} parentId={folderId} />}
      {currentModal === "cityPlanner" && currentAccount && (
        <NewCityPlanner onClose={() => setCurrentModal(null)} accountId={currentAccount.ID} parentFolder={folder} siblings={siblings} />
      )}
      {currentModal === "subscriptionError" && (
        <Modal
          open
          data-testid="planner-project-error-modal"
          variant="secondary"
          header="Oops..."
          content={<SubscriptionErrorMessage />}
          actions={
            <Button data-testid="back-button" color="primary" onClick={() => setCurrentModal(null)}>
              Back
            </Button>
          }
        />
      )}
    </>
  );
};

export default NewItemList;
